import {
  RugReportSectionId,
  type SectionAddendum,
} from 'pages/reporting/reports/rug/rugReportData'

import { getAddendumPages } from './addendumContent'
import { getAttachmentFileKind } from './attachmentFiles'
import { inspectAttachmentPdf } from './attachmentPdf'

interface Dependencies {
  fetchBytes: (path: string, signal: AbortSignal) => Promise<ArrayBuffer>
  encodeImage: (bytes: ArrayBuffer, signal: AbortSignal) => Promise<string>
}

export async function prepareAddendums(
  addendums: SectionAddendum[],
  dependencies: Dependencies,
  signal: AbortSignal
) {
  const imageSources = new Map<string, string>()
  const pdfSources = new Map<string, Uint8Array>()
  const fileBytes = new Map<string, ArrayBuffer>()
  const attachmentIds = new Set<string>()
  for (const addendum of addendums) {
    if (
      [
        RugReportSectionId.SECTION_ADDENDUMS,
        RugReportSectionId.COVER,
        RugReportSectionId.TABLE_OF_CONTENTS,
        RugReportSectionId.REVIEW,
        RugReportSectionId.INTRO_TO_RUG,
      ].some((id) => id === addendum.sectionId)
    ) {
      throw new Error(
        `Choose a report content section or custom section for ${addendum.sectionId}`
      )
    }
    if (getAddendumPages(addendum).length === 0)
      throw new Error(
        `Add an attachment to ${addendum.sectionId} or select blank pages`
      )
    if (addendum.mode !== 'attachments') continue
    for (const attachment of addendum.attachments ?? []) {
      signal.throwIfAborted()
      if (attachmentIds.has(attachment.id))
        throw new Error(`Duplicate attachment: ${attachment.fileName}`)
      attachmentIds.add(attachment.id)
      try {
        const bytes =
          fileBytes.get(attachment.path) ??
          (await dependencies.fetchBytes(attachment.path, signal))
        signal.throwIfAborted()
        fileBytes.set(attachment.path, bytes)
        if (
          getAttachmentFileKind({
            name: attachment.fileName,
            size: bytes.byteLength,
          }) !== attachment.kind
        )
          throw new Error('File type does not match the saved attachment')
        if (attachment.kind === 'pdf') {
          if ((await inspectAttachmentPdf(bytes)) !== attachment.pageCount)
            throw new Error('The PDF page count changed. Upload it again.')
          pdfSources.set(attachment.id, new Uint8Array(bytes))
        } else {
          imageSources.set(
            attachment.id,
            await dependencies.encodeImage(bytes, signal)
          )
        }
        signal.throwIfAborted()
      } catch (error) {
        if (signal.aborted) signal.throwIfAborted()
        throw new Error(
          `Could not prepare ${attachment.fileName}: ${error instanceof Error ? error.message : 'Please upload it again'}`
        )
      }
    }
  }
  return { imageSources, pdfSources }
}
